import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";

const ROLES = [
  { value: "superadmin", label: "Super Admin" },
  { value: "admin", label: "Admin / Studio" },
  { value: "secretary", label: "Segreteria" },
];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "secretary" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", form);
      setShowCreate(false);
      setForm({ email: "", password: "", name: "", role: "secretary" });
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (uid) => {
    if (!window.confirm("Eliminare questo utente?")) return;
    try {
      await api.delete(`/users/${uid}`);
      setUsers(users.filter((u) => u.id !== uid));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="max-w-4xl space-y-6 fade-up" data-testid="users-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Gestione</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#1C2321] mt-2">Utenti</h1>
          <p className="text-[#56635D] mt-2">SuperAdmin, Admin/Studio e Segreteria.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-user-button" className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 rounded-lg">
          <Plus size={16} weight="bold" /> Nuovo utente
        </button>
      </div>

      {error && <div className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{error}</div>}

      {showCreate && (
        <form onSubmit={create} className="elev-card p-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="create-user-form">
          <SField label="Nome"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" className="input" /></SField>
          <SField label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" className="input" /></SField>
          <SField label="Password (min 8)"><input required minLength={8} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" className="input" /></SField>
          <SField label="Ruolo">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role" className="input">
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </SField>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" data-testid="create-user-submit" className="bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 rounded-lg">Crea utente</button>
            <button type="button" onClick={() => setShowCreate(false)} className="bg-[#EAF0EC] text-brand-700 px-5 py-2.5 rounded-lg">Annulla</button>
          </div>
        </form>
      )}

      <div className="elev-card divide-y divide-[#E6E8E6]" data-testid="users-list">
        {loading ? <div className="p-6 text-[#56635D]">Caricamento…</div> : users.length === 0 ? <div className="p-6 text-[#56635D]">Nessun utente.</div> : users.map((u) => (
          <div key={u.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`user-${u.id}`}>
            <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center"><UserCircle size={22} weight="duotone" className="text-brand-700" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{u.name}</div>
              <div className="text-xs text-[#56635D]">{u.email}</div>
            </div>
            <span className="text-xs font-medium bg-brand-100 text-brand-700 px-2.5 py-1 rounded-full">{ROLES.find((r) => r.value === u.role)?.label || u.role}</span>
            <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} className={`text-xs px-2.5 py-1 rounded-full ${u.is_active ? "bg-brand-50 text-brand-700" : "bg-[#A84A4A]/10 text-[#A84A4A]"}`}>
              {u.is_active ? "Attivo" : "Disattivato"}
            </button>
            <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="text-[#A84A4A]"><Trash size={16} weight="duotone" /></button>
          </div>
        ))}
      </div>

      <style>{`.input{width:100%;padding:.65rem .85rem;border:1px solid #E6E8E6;border-radius:.5rem;background:#fff} .input:focus{border-color:#2C4A3B;outline:none}`}</style>
    </div>
  );
}

function SField({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#56635D] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
